import React, { useState, useContext } from 'react';
import { DateTimePicker } from '@mui/x-date-pickers';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, 
  Button, Typography, TableSortLabel } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import * as Constants from '../Constants';
import { AuthContext } from '../AuthContextProvider';
import ErrorDialog from '../ErrorDialog';
import moment from 'moment-timezone';
import { rpc } from '../utils/RpcUtils';
import { getTZDateTimeAsString } from '../utils/DateUtils';
import '../App.css';

const Usage = () => {
  const [startDate, setStartDate] = useState(moment());
  const [endDate, setEndDate] = useState(moment());
  const [errorMessage, setErrorMessage] = useState('');
  const [usages, setUsages] = useState([]);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('');
  
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const { user } = useContext(AuthContext);

  moment.tz.setDefault(user._site.timezone);

  const searchUsages = async () => {
    const params = {
      when: {$gte: startDate, $lte: endDate},
      _site: user._site._id,
    };
    await rpc({
      method: 'GetUsages',
      params,
      setErrorMessage,
      setOpenErrorDialog,
      onSuccess: setUsages,
    });
  };

  const handleStartDateChange = (date) => {
    setStartDate(date);
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
  };

  const handleClose = () => {
    setOpenErrorDialog(false);
  };

  const handleSort = (column) => {
    const isAsc = orderBy === column && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(column);
  
    const sortedUsages = [...usages].sort((a, b) => {
      let valueA = a[column] ?? '';  // Default to empty string if undefined/null
      let valueB = b[column] ?? '';  
  
      if (column === 'when') {
        return isAsc 
          ? new Date(valueA) - new Date(valueB) 
          : new Date(valueB) - new Date(valueA);
      } else {
        return isAsc 
          ? valueA.toString().localeCompare(valueB.toString()) 
          : valueB.toString().localeCompare(valueA.toString());
      }
    });
  
    setUsages(sortedUsages);
  };

  return (
    <div className="Usage">
      <Typography variant="h6" gutterBottom>
        Usage Report
      </Typography>
      <LocalizationProvider adapterLocale="en" dateAdapter={AdapterMoment} dateLibInstance={moment}>
        <DateTimePicker className="DatePicker"
          label="Start Date"
          value={startDate}
          onChange={handleStartDateChange}
          slotProps={{ textField: { variant: 'outlined' } }}
          format={Constants.DATE_TIME_FORMAT}
        />
      </LocalizationProvider>
      <LocalizationProvider adapterLocale="en" dateAdapter={AdapterMoment} dateLibInstance={moment}>
        <DateTimePicker className="DatePicker"
          label="End Date"
          value={endDate}
          onChange={handleEndDateChange}
          slotProps={{ textField: { variant: 'outlined' } }}
          format={Constants.DATE_TIME_FORMAT}
        />
      </LocalizationProvider>
      <Button className="Button" variant="contained" color="primary" onClick={searchUsages}>
          Search
      </Button>
      <TableContainer className="TableContainer" component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel active={orderBy === 'who'} direction={order} onClick={() => handleSort('who')}>
                  <b>Who</b>
                </TableSortLabel>
              </TableCell>
              <TableCell><b>What</b></TableCell>
              <TableCell>
                <TableSortLabel active={orderBy === 'when'} direction={order} onClick={() => handleSort('when')}>
                  <b>When</b>
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel active={orderBy === 'extra'} direction={order} onClick={() => handleSort('extra')}>
                  <b>Extra</b>
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {usages.map((usage, index) => (
              <TableRow key={index}>
                <TableCell>{usage.who}</TableCell>
                <TableCell>{usage.what}</TableCell>
                <TableCell>{getTZDateTimeAsString(usage.when, user._site.timezone)}</TableCell>
                <TableCell>{usage.extra}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <ErrorDialog open={openErrorDialog} onErrorDialogClose={handleClose} errorMessage={errorMessage} />
    </div>
  );
};

export default Usage;
